import { CultureOverrides, PimLocationQuery } from '@/models/Global'
import { RegisteredTaxonomies, RegisteredTaxonomy, SimplifiedCategory, SimplifiedTaxonomicObject, TaxonomicTypeMappings } from '@/models/Taxonomy'
import ApiService from '@/services/ApiService'
import DataService from '@/services/DataService'
import { arrayToMap, arrayToRecord, toComparable } from '@/utils/tools'
import { defineStore } from 'pinia'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { useDefaultStore } from '.'
import Message from '@/models/Message'
import { sortCategories } from '@/utils/productUtils'

type TaxonomicEntries<T extends SimplifiedTaxonomicObject = SimplifiedTaxonomicObject> = {
  promise?: Promise<T[] | undefined>
  data?: T[]
  invalidated?: boolean // set to invalidate cache and refresh on next access. causes current data to be displayed until fresh is fetched
  date?: Date
}

export const useTaxonomiesStore = defineStore('cached-taxonomies-store', () => {
  const store = useDefaultStore()

  // --------------
  // -- Internal --
  // --------------

  const taxonomicData = RegisteredTaxonomies.reduce(
    (acc, taxonomy) => {
      acc[taxonomy] = ref({})
      return acc
    },
    {} as {
      [K in RegisteredTaxonomy]: Ref<TaxonomicEntries<TaxonomicTypeMappings[K]>>
    },
  )

  /** Fetches fresh taxonomic data from the API */
  const fetchTaxonomy = async <T extends SimplifiedTaxonomicObject>(
    taxonomy: RegisteredTaxonomy,
    cultureOverrides?: CultureOverrides | null,
    includeMatches?: boolean,
    query?: PimLocationQuery,
  ) => {
    try {
      const queryParams = DataService.getParams(query, cultureOverrides)
      includeMatches ? queryParams.set('includeMatches', '1') : undefined
      return await ApiService.get<T[]>(`taxonomies/${taxonomy}?${queryParams.toString()}`, undefined, 'v2')
    } catch (err) {
      store.addMessage(new Message(`Kunde inte hämta ${taxonomy}`, err, 'danger'))
    }
  }

  const createComputedTaxonomicProperty = <K extends RegisteredTaxonomy>(taxonomy: K, ref: Ref<TaxonomicEntries<TaxonomicTypeMappings[K]> | undefined>) =>
    computed(() => {
      ref.value?.invalidated // capture reactivity
      getTaxonomicData(taxonomy) // ensure data population
      return ref.value?.data || []
    })

  // ------------
  // -- Public --
  // ------------

  // -- Reactive computed properties - use in reactive flows, use async equivalents in functions --

  const taxonomies = RegisteredTaxonomies.reduce(
    (acc, taxonomy) => {
      acc[taxonomy] = createComputedTaxonomicProperty(taxonomy, taxonomicData[taxonomy]) as any
      return acc
    },
    {} as { [K in RegisteredTaxonomy]: ReturnType<typeof createComputedTaxonomicProperty<K>> },
  )

  const taxonomiesByComparable = RegisteredTaxonomies.reduce(
    (acc, taxonomy) => {
      acc[taxonomy] = computed(() => arrayToMap(taxonomies[taxonomy].value || [], 'defaultName', toComparable)) as any
      return acc
    },
    {} as { [K in RegisteredTaxonomy]: ComputedRef<Map<string, TaxonomicTypeMappings[K]>> },
  )

  const taxonomiesById = RegisteredTaxonomies.reduce(
    (acc, taxonomy) => {
      acc[taxonomy] = computed(() => {
        return arrayToMap(taxonomies[taxonomy].value || [], 'id')
      }) as any
      return acc
    },
    {} as { [K in RegisteredTaxonomy]: ComputedRef<Map<string, TaxonomicTypeMappings[K]>> },
  )

  const categoriesByParentIdAndName = computed(() => {
    return taxonomies.categories.value.reduce<Record<string, SimplifiedCategory | undefined>>((acc, c) => {
      const key = getByParentIdAndNameKey(c.defaultName, c.parentId)
      acc[key] = c
      return acc
    }, {})
  })

  // -- Functions --

  const invalidateTaxonomicCache = (taxonomy?: RegisteredTaxonomy) => {
    if (taxonomy) {
      if (taxonomicData[taxonomy].value) {
        taxonomicData[taxonomy].value.invalidated = true
      }
    } else {
      RegisteredTaxonomies.forEach((taxonomy) => {
        if (taxonomicData[taxonomy].value) {
          taxonomicData[taxonomy].value.invalidated = true
        }
      })
    }
  }

  /**
   * Returns cache or fetches async data
   * To ensure fresh data, run invalidateTaxonomicCache(<taxonomy>) first
   */
  async function getTaxonomicData<K extends RegisteredTaxonomy>(
    taxonomy: K,
    cultureOverrides?: CultureOverrides | null,
    includeMatches?: boolean,
    query?: PimLocationQuery,
  ) {
    const dataRef = taxonomicData[taxonomy]
    type T = TaxonomicTypeMappings[K]

    if (cultureOverrides !== undefined || includeMatches || query) {
      // don't cache non default data
      return fetchTaxonomy<T>(taxonomy, cultureOverrides, includeMatches, query)
    }

    const staleAfterMs = (store.settings?.app.taxonomicCacheStaleAfterSeconds ?? 60) * 1000

    const isStale = dataRef.value?.date && Date.now() - dataRef.value.date.getTime() > staleAfterMs

    if ((!dataRef.value?.data || dataRef.value.invalidated || isStale) && !dataRef.value.promise) {
      dataRef.value.promise = fetchTaxonomy<T>(taxonomy, cultureOverrides, includeMatches, query)
      const data = await dataRef.value.promise

      if (data) {
        if (taxonomy === 'categories') {
          sortCategories(data)
        }

        dataRef.value = { data, date: new Date() }
        delete dataRef.value.promise
      }
    }
    await dataRef.value.promise
    return dataRef.value?.data
  }

  const getTaxonomicDataById = async <K extends RegisteredTaxonomy>(taxonomy: K) => {
    await getTaxonomicData(taxonomy)
    return taxonomiesById[taxonomy].value as Map<string, TaxonomicTypeMappings[K]>
  }

  /**
   * Returns cache or fetches async data
   * To ensure fresh data, run invalidateTaxonomicCache() first
   */
  const preFetchTaxonomicData = async () => Promise.all(RegisteredTaxonomies.map((taxonomy) => getTaxonomicData(taxonomy)))

  const getByParentIdAndNameKey = (defaultName: string | number, parentId: string | null | undefined) => `${defaultName}_${parentId || ''}`

  const findCategory = (defaultName: string | number, parentId: string | null | undefined) =>
    categoriesByParentIdAndName.value[getByParentIdAndNameKey(defaultName, parentId)]

  return {
    taxonomies,
    taxonomiesByComparable,
    taxonomiesById,
    clearTaxonomicCache: invalidateTaxonomicCache,
    preFetchTaxonomicData,
    getTaxonomicData,
    getTaxonomicDataById,
    findCategory,
  }
})
